import type { RouteLocationNormalized } from 'vue-router';
import { defineStore } from 'pinia';
import {
  DEFAULT_ROUTE,
  DEFAULT_ROUTE_NAME,
  REDIRECT_ROUTE_NAME,
} from '@/router/constants';
import { isString } from '@/utils/is';
import { getRouteTabKey } from '@/utils/tab-instance';
import { TabBarState, TagProps } from './types';

const formatTag = (route: RouteLocationNormalized): TagProps => {
  const { name, meta, fullPath, query } = route;
  return {
    title: meta.locale || '',
    name: String(name),
    fullPath,
    tabId: getRouteTabKey(route),
    query,
    ignoreCache: meta.ignoreCache,
  };
};

const DEFAULT_TAG: TagProps = {
  title: '首页',
  name: DEFAULT_ROUTE_NAME,
  fullPath: DEFAULT_ROUTE,
  tabId: DEFAULT_ROUTE,
  query: {},
  ignoreCache: false,
};

const BAN_LIST = [REDIRECT_ROUTE_NAME];

const useAppStore = defineStore('tabBar', {
  state: (): TabBarState => ({
    cacheTabList: new Set([DEFAULT_ROUTE_NAME]),
    tagList: [DEFAULT_TAG],
  }),

  getters: {
    getTabList(): TagProps[] {
      return this.tagList;
    },
    getCacheList(): string[] {
      return Array.from(this.cacheTabList);
    },
  },

  actions: {
    rebuildCacheList(tags: TagProps[]) {
      this.cacheTabList.clear();
      tags
        .filter((item) => !item.ignoreCache)
        .map((item) => item.name)
        .forEach((name) => this.cacheTabList.add(name));
    },
    updateTabList(route: RouteLocationNormalized) {
      if (BAN_LIST.includes(route.name as string)) return;
      const nextTag = formatTag(route);
      const tagIndex = this.tagList.findIndex(
        (item) => item.tabId === nextTag.tabId
      );

      if (tagIndex > -1) {
        this.tagList.splice(tagIndex, 1, nextTag);
      } else {
        this.tagList.push(nextTag);
      }

      if (!route.meta.ignoreCache) {
        this.cacheTabList.add(route.name as string);
      }
    },
    deleteTag(idx: number, tag: TagProps) {
      this.tagList.splice(idx, 1);
      this.rebuildCacheList(this.tagList);
    },
    addCache(name: string) {
      if (isString(name) && name !== '') this.cacheTabList.add(name);
    },
    deleteCache(tag: TagProps) {
      this.cacheTabList.delete(tag.name);
    },
    freshTabList(tags: TagProps[]) {
      this.tagList = tags;
      this.rebuildCacheList(this.tagList);
    },
    resetTabList() {
      this.tagList = [DEFAULT_TAG];
      this.rebuildCacheList(this.tagList);
    },
  },
});

export default useAppStore;
